import { ChangeDetectionStrategy, Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { NGXLogger } from 'ngx-logger';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { ComanageService } from 'src/app/comanage/comanage.service';
import { Timeslot } from 'src/app/models/Timeslot';

@Component({
    selector: 'app-link-invoice-dialog',
    templateUrl: './link-invoice-dialog.component.html',
    styleUrls: ['./link-invoice-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class LinkInvoiceDialogComponent implements OnInit {
    private subscriptions = new Subscription();
    isLoading = false;

    timeslots: Timeslot[] = [];
    comanageInvoices: any[] = [];
    filteredComanageInvoices: any[] = [];
    existingInvoiceSearch = '';
    selectedExistingInvoiceNumber = '';

    constructor(
        public dialogRef: MatDialogRef<LinkInvoiceDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: any,
        private comanageService: ComanageService,
        private toastr: ToastrService,
        private logger: NGXLogger,
    ) {}

    ngOnInit(): void {
        this.timeslots = (this.data?.projectTimeslots || []) as Timeslot[];
        this.loadComanageInvoices();
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    private loadComanageInvoices(): void {
        this.isLoading = true;
        this.subscriptions.add(
            this.comanageService.getInvoices().subscribe(
                (response: any) => {
                    const parsed = this.parseComanageInvoicesPayload(response)
                        .filter((invoice: any) => !!this.getComanageInvoiceNumber(invoice))
                        .sort((left: any, right: any) => {
                            return this.compareInvoiceNumbersDesc(this.getComanageInvoiceNumber(left), this.getComanageInvoiceNumber(right));
                        });

                    this.comanageInvoices = parsed;
                    this.applyExistingInvoiceFilter();
                    this.isLoading = false;
                },
                (error) => {
                    this.logger.warn('Failed to load Comanage invoices for linking', error);
                    this.comanageInvoices = [];
                    this.filteredComanageInvoices = [];
                    this.isLoading = false;
                    this.toastr.error('Bestaande facturen konden niet geladen worden.');
                },
            ),
        );
    }

    private parseComanageInvoicesPayload(payload: any): any[] {
        if (Array.isArray(payload)) {
            return payload;
        }

        if (typeof payload === 'string') {
            try {
                const parsed = JSON.parse(payload);
                return Array.isArray(parsed) ? parsed : [];
            } catch {
                return [];
            }
        }

        if (Array.isArray(payload?.data)) {
            return payload.data;
        }

        return [];
    }

    private normalize(value: any): string {
        if (value === null || value === undefined) {
            return '';
        }
        return String(value).trim();
    }

    private compareInvoiceNumbersDesc(left: string, right: string): number {
        const parseParts = (value: string): number[] => {
            const normalized = this.normalize(value);
            if (!normalized) {
                return [];
            }
            const numericParts = normalized.match(/\d+/g) || [];
            return numericParts.map((part) => Number(part));
        };

        const leftParts = parseParts(left);
        const rightParts = parseParts(right);
        const maxLength = Math.max(leftParts.length, rightParts.length);

        for (let index = 0; index < maxLength; index++) {
            const leftValue = Number.isFinite(leftParts[index]) ? leftParts[index] : -1;
            const rightValue = Number.isFinite(rightParts[index]) ? rightParts[index] : -1;
            if (leftValue !== rightValue) {
                return rightValue - leftValue;
            }
        }

        return right.localeCompare(left, undefined, { sensitivity: 'base', numeric: true });
    }

    getComanageInvoiceNumber(invoice: any): string {
        return this.normalize(invoice?.invoice_number ?? invoice?.number ?? invoice?.invoiceNumber ?? invoice?.id);
    }

    getComanageInvoiceId(invoice: any): string {
        return this.normalize(invoice?.id ?? invoice?.uuid ?? invoice?.invoice_id ?? invoice?.invoiceId);
    }

    getComanageInvoiceStatus(invoice: any): string {
        return this.normalize(invoice?.status ?? invoice?.state);
    }

    getComanageInvoiceDate(invoice: any): string {
        return this.normalize(invoice?.date ?? invoice?.created_at ?? invoice?.invoice_date);
    }

    private buildComanageEditLink(invoice: any): string | null {
        if (!invoice) {
            return null;
        }

        const candidates = [invoice?.link, invoice?.url, invoice?.html_url, invoice?.edit_url, invoice?.pdf, invoice?.pdf_url, invoice?.download_pdf_url]
            .filter((value) => !!value)
            .map((value) => String(value).trim());

        for (const candidate of candidates) {
            const match = candidate.match(/(invoices|creditnotes)\/(\d+)(?:\/[^?#]*)?/i);
            if (!match) {
                continue;
            }

            const entity = match[1].toLowerCase();
            const number = match[2];

            const candidateOriginMatch = candidate.match(/https?:\/\/[^\/]+/i);
            const candidateOrigin = candidateOriginMatch ? candidateOriginMatch[0] : '';
            const isApiHost = candidateOrigin.includes('api.comanage.me');
            const appOrigin = !isApiHost && candidateOrigin.length > 0 ? candidateOrigin : null;
            const origin = appOrigin || this.getComanageAppOrigin();

            return `${origin}/${entity}/${number}/edit`;
        }

        const invoiceId = this.getComanageInvoiceId(invoice);
        if (!invoiceId) {
            return null;
        }

        return `${this.getComanageAppOrigin()}/invoices/${invoiceId}/edit`;
    }

    private getComanageAppOrigin(): string {
        const browserOrigin = typeof window !== 'undefined' ? window.location.origin : '';
        if (browserOrigin.includes('.comanage.me')) {
            return browserOrigin;
        }
        return 'https://app.comanage.me';
    }

    getComanageInvoiceDisplay(invoice: any): string {
        const invoiceNumber = this.getComanageInvoiceNumber(invoice);
        const title = this.normalize(invoice?.title || invoice?.subject);
        const date = this.getComanageInvoiceDate(invoice);
        const status = this.getComanageInvoiceStatus(invoice);

        const right = [title, date, status].filter((value) => value.length > 0).join(' · ');
        return right.length > 0 ? `${invoiceNumber} — ${right}` : invoiceNumber;
    }

    applyExistingInvoiceFilter(): void {
        const filter = (this.existingInvoiceSearch || '').trim().toLowerCase();
        if (!filter) {
            this.filteredComanageInvoices = [...this.comanageInvoices].slice(0, 200);
            return;
        }

        this.filteredComanageInvoices = this.comanageInvoices
            .filter((invoice) => {
                const haystack = [
                    this.getComanageInvoiceNumber(invoice),
                    this.getComanageInvoiceId(invoice),
                    this.normalize(invoice?.title || ''),
                    this.normalize(invoice?.subject || ''),
                    this.getComanageInvoiceStatus(invoice),
                    this.getComanageInvoiceDate(invoice),
                ]
                    .join(' ')
                    .toLowerCase();

                return haystack.includes(filter);
            })
            .slice(0, 200);
    }

    private getSelectedExistingInvoice(): any | null {
        const selected = (this.selectedExistingInvoiceNumber || '').trim();
        if (!selected) {
            return null;
        }

        return this.comanageInvoices.find((invoice) => this.getComanageInvoiceNumber(invoice) === selected) || null;
    }

    linkInvoice(): void {
        const selectedInvoice = this.getSelectedExistingInvoice();
        if (!selectedInvoice) {
            this.toastr.error('Kies eerst een bestaande factuur.');
            return;
        }

        if ((this.timeslots || []).length === 0) {
            this.toastr.error('Geen timeslots beschikbaar om te linken.');
            return;
        }

        const invoiceNumber = this.getComanageInvoiceNumber(selectedInvoice);
        const invoiceId = this.getComanageInvoiceId(selectedInvoice) || undefined;
        const invoiceStatus = this.getComanageInvoiceStatus(selectedInvoice) || undefined;
        const invoiceDate = this.getComanageInvoiceDate(selectedInvoice) || undefined;
        const pdfUrl = this.normalize(selectedInvoice?.pdf || selectedInvoice?.pdf_url || selectedInvoice?.download_pdf_url) || undefined;
        const comanageLink = this.buildComanageEditLink(selectedInvoice) || undefined;

        this.dialogRef.close({
            timeslots: this.timeslots,
            invoiceNumber,
            invoiceId,
            invoiceStatus,
            invoiceDate,
            pdfUrl,
            comanageLink,
        });
    }

    closeDialog(): void {
        this.dialogRef.close();
    }
}
